import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FaRegEdit,
  FaBoxOpen,
  FaTruck,
  FaMotorcycle,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { ShopContext } from "../context/ShopContext";
import { BillTemplate } from "../components/BillTemplate";

const OrderDetails = () => {
  const navigate = useNavigate();
  const { products, userInfo, currency,delivery_fee } = useContext(ShopContext);
  const [orderItem, setOrderItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  const orderData = localStorage.getItem("orderData");
  const [orderId, setOrderId] = useState("");

  useEffect(() => {
    if (orderData) {
      try {
        const parsedData = JSON.parse(orderData);
        // Find product details by productId
        const product = products.find((p) => p._id === parsedData.productId);
        setOrderItem({
          ...parsedData,
          // Merge product details if found
          ...(product
            ? {
                name: product.name,
                image: product.image,
                price: product.price,
                description: product.description,
                category: product.category,
                subCategory: product.subCategory,
              }
            : {}),
        });
        const date = new Date();
        const dateStr = `${date.getFullYear()}${String(date.getMonth()+1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`;
        const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
        const uniqueId = `${dateStr}-${parsedData.productId?.slice(-5) || "PID"}-${randomStr}`;
        setOrderId(uniqueId);
      } catch (error) {
        console.error("Error parsing order data:", error);
      }
    }
    setLoading(false);
  }, [products]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  if (!orderItem) {
    return (
      <div className="flex flex-col items-center justify-center h-screen animate-fade-in">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md transform transition-all hover:scale-105">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 mx-auto text-red-500 animate-bounce"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-xl font-bold mt-4 text-gray-800">
            Order Not Found
          </h2>
          <p className="mt-2 text-gray-600">
            We couldn&apos;t find the order details you&apos;re looking for.
          </p>
          <button
            onClick={() => navigate("/orders")}
            className="mt-6 px-6 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all transform hover:scale-105 shadow-md"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  // Find product details for this order item
  const product = products.find((p) => p._id === orderItem.productId) || {};

  const subtotal = product.price * orderItem.quantity + delivery_fee;

  // Status Flow Configuration (Backend-Aligned)
  const statusConfig = {
    "Order Placed": {
      color: "bg-purple-100 text-purple-800",
      icon: <FaRegEdit className="inline" />,
      progress: 20,
    },
    Packing: {
      color: "bg-yellow-100 text-yellow-800",
      icon: <FaBoxOpen className="inline" />,
      progress: 40,
    },
    Shipped: {
      color: "bg-blue-100 text-blue-800",
      icon: <FaTruck className="inline" />,
      progress: 60,
    },
    "Out for Delivery": {
      color: "bg-orange-100 text-orange-800",
      icon: <FaMotorcycle className="inline" />,
      progress: 80,
    },
    Delivered: {
      color: "bg-green-100 text-green-800",
      icon: <FaCheck className="inline" />,
      progress: 100,
    },
    Cancelled: {
      color: "bg-red-100 text-red-800",
      icon: <FaTimes className="inline" />,
      progress: 0,
    },
  };

  const status = statusConfig[orderItem.status] || {
    color: "bg-gray-100 text-gray-800",
    icon: "ℹ️",
    progress: 0,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>{`Order #${orderId} - Sahla Curtains`}</title>
      </Helmet>

      <div className="max-w-4xl mx-auto animate-fade-in-up">
        <button
          onClick={() => navigate("/orders")}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-8 transition-all transform hover:-translate-x-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to Orders
        </button>

        {/* Order Card */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden transition-all hover:shadow-2xl transform hover:-translate-y-1 duration-300">
          {/* Order Header */}
          <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Order ID:{" "}
                  <span className="text-blue-700">{orderId}</span>
                </h1>
                <p className="mt-1 text-sm text-gray-500 flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Placed on{" "}
                  {new Date(orderItem.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
              <div
                className={`mt-3 sm:mt-0 px-3 py-1 rounded-full text-sm font-medium flex items-center ${status.color} animate-pulse`}
              >
                <span className="mr-1">{status.icon}</span>
                {orderItem.status}
              </div>
            </div>
          </div>

          {/* Order Progress Bar */}
          <div className="bg-gray-100 px-6 py-3">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-green-600 h-2.5 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${status.progress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span
                className={`${
                  status.progress >= 20 ? "font-medium text-purple-600" : ""
                }`}
              >
                Placed
              </span>
              <span
                className={`${
                  status.progress >= 40 ? "font-medium text-yellow-600" : ""
                }`}
              >
                Packing
              </span>
              <span
                className={`${
                  status.progress >= 60 ? "font-medium text-blue-600" : ""
                }`}
              >
                Shipped
              </span>
              <span
                className={`${
                  status.progress >= 80 ? "font-medium text-orange-600" : ""
                }`}
              >
                Out for Delivery
              </span>
              <span
                className={`${
                  status.progress === 100 ? "font-medium text-green-600" : ""
                }`}
              >
                Delivered
              </span>
            </div>
          </div>

          {/* Product Details */}
          <div className="px-6 py-5">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
              Product Details
            </h2>
            <div className="flex flex-col md:flex-row gap-6">
              {product.image && (
                <div className="md:w-1/3 group">
                  <img
                    src={product.image[0]}
                    alt={product.name}
                    className="w-full h-auto rounded-lg object-cover transition-transform duration-300 group-hover:scale-105 shadow-md"
                  />
                </div>
              )}
              <div className="md:w-2/3">
                <h3 className="text-xl font-medium text-gray-800">
                  {product.name}
                </h3>
                <div className="mt-2 flex items-center space-x-4">
                  {product.category && (
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {product.category}
                    </span>
                  )}
                  {product.subCategory && (
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {product.subCategory}
                    </span>
                  )}
                </div>
                <p className="text-gray-600 mt-2">{product.description}</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                    <p className="text-sm text-gray-500">Unit Price</p>
                    <p className="font-medium text-gray-600">
                      {currency}
                      {product.price}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                    <p className="text-sm text-gray-500">Quantity</p>
                    <p className="font-medium">{orderItem.quantity}</p>
                  </div>
                  {orderItem.size && (
                    <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                      <p className="text-sm text-gray-500">Size</p>
                      <p className="font-medium">{orderItem.size}</p>
                    </div>
                  )}
                  {orderItem.color && (
                    <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                      <p className="text-sm text-gray-500">Color</p>
                      <span
                        className="inline-block w-6 h-6 rounded-full border border-gray-300 align-middle mr-2"
                        style={{ backgroundColor: orderItem.color }}
                      ></span>
                      <span className="font-medium">{orderItem.color}</span>
                    </div>
                  )}
                  {orderItem.measurements ? (
                    <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                      <p className="text-sm text-gray-500 flex items-center">
                        Measurements
                        <button
                          className="ml-2 text-blue-600 underline text-xs hover:text-blue-800 focus:outline-none"
                          onClick={() => setShowMeasurementModal(true)}
                          type="button"
                        >
                          Learn More
                        </button>
                      </p>
                      <p className="font-medium">
                        Height: {orderItem.measurements.H} {orderItem.measurements.measurementUnit}, Width: {orderItem.measurements.W} {orderItem.measurements.measurementUnit}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                      <p className="text-sm text-gray-500 flex items-center">
                        Measurements
                        <button
                          className="ml-2 text-blue-600 underline text-xs hover:text-blue-800 focus:outline-none"
                          onClick={() => setShowMeasurementModal(true)}
                          type="button"
                        >
                          Learn More
                        </button>
                      </p>
                      <p className="font-medium text-gray-500">
                        Our team will personally visit your location to take precise measurements.
                      </p>
                    </div>
                  )}
                  <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                    <p className="text-sm text-gray-500">Subtotal</p>
                    <p className="font-medium text-green-600">
                      {currency}
                      {subtotal.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="border-t border-gray-200 px-6 py-5 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              Order Summary
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-medium">
                  {orderItem.paymentMethod || "Cash on Delivery"}
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-500">Payment Status</p>
                <p
                  className={`font-medium ${
                    orderItem.payment === "Done"
                      ? "text-green-600"
                      : "text-yellow-600"
                  }`}
                >
                  {orderItem.payment ? "Paid ✅" : "Pending ⏳"}
                </p>
              </div>
            </div>
          </div>

          {/* Total */}
          <div className="bg-gradient-to-r from-gray-100 to-gray-300 px-6 py-5 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <p className="text-lg font-medium text-gray-900">Total Amount</p>
              <p className="text-2xl font-bold text-gray-600 animate-bounce">
                {orderItem.currency || "$"}
                {subtotal.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-8">
        <button
          className="bg-gray-900 text-white px-6 py-2 rounded-md shadow hover:bg-gray-800 transition-colors font-semibold"
          onClick={() => {
            // Use window.print for high clarity print/download
            const billNode = document.createElement("div");
            const billTemplate = document.getElementById("invoice-section");
            if (billTemplate) {
              billNode.innerHTML = billTemplate.innerHTML;
              const printWindow = window.open("", "", "height=800,width=900");
              printWindow.document.write("<html><head><title>Order Bill</title>");
              printWindow.document.write(
                '<style>body{font-family:sans-serif;} img{max-width:100px;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #ddd;padding:8px;} th{background:#f3f3f3;}</style>'
              );
              printWindow.document.write("</head><body>");
              printWindow.document.write(billNode.innerHTML);
              printWindow.document.write("</body></html>");
              printWindow.document.close();
              printWindow.focus();
              setTimeout(() => {
                printWindow.print();
                printWindow.close();
              }, 500);
            }
          }}
          type="button"
        >
          Download Bill
        </button>
      </div>
      <div
        id="invoice-section"
        style={{ position: "absolute", left: "-9999px", top: 0 }}
        aria-hidden="true"
      >
        <BillTemplate
          orderItem={orderItem}
          product={product}
          userInfo={userInfo}
          currency={currency}
        />
      </div>

      {/* Measurement Modal */}
      {showMeasurementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full animate-fade-in">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">How Measurements Work</h3>
            <p className="text-gray-700 mb-4">
              We will visit your destination to take accurate measurements for your curtains, ensuring a perfect fit and finish. After our visit, your order will be updated with the exact details. This personalized service guarantees your satisfaction and peace of mind.
            </p>
            <button
              className="mt-2 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
              onClick={() => setShowMeasurementModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
